import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type Currency = 'USD' | 'EUR' | 'GBP';

export interface CurrencyConfig {
  code: Currency;
  symbol: string;
  name: string;
  country: string;
  flag: string;
  exchangeRate: number; // Rate relative to EUR (base currency)
  locale: string;
}

export const CURRENCIES: Record<Currency, CurrencyConfig> = {
  USD: {
    code: 'USD',
    symbol: '$',
    name: 'US Dollar',
    country: 'United States',
    flag: '🇺🇸',
    exchangeRate: 1.18, // 1 EUR = 1.18 USD (approximate)
    locale: 'en-US'
  },
  EUR: {
    code: 'EUR',
    symbol: '€',
    name: 'Euro',
    country: 'European Union',
    flag: '🇪🇺',
    exchangeRate: 1, // EUR is now the base currency
    locale: 'en-EU'
  },
  GBP: {
    code: 'GBP',
    symbol: '£',
    name: 'British Pound',
    country: 'United Kingdom',
    flag: '🇬🇧',
    exchangeRate: 0.86, // 1 EUR = 0.86 GBP (approximate)
    locale: 'en-GB'
  }
};

interface CurrencyContextType {
  currency: Currency;
  setCurrency: (currency: Currency) => void;
  formatPrice: (price: number) => string;
  convertPrice: (price: number, fromCurrency?: Currency) => number;
  currencies: CurrencyConfig[];
  loading: boolean;
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (context === undefined) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

interface CurrencyProviderProps {
  children: ReactNode;
}

export const CurrencyProvider: React.FC<CurrencyProviderProps> = ({ children }) => {
  const [currency, setCurrencyState] = useState<Currency>('EUR');
  const [loading, setLoading] = useState(true);

  const getHostName = (): string => {
    if (typeof window === 'undefined') return '';
    return (window.location.hostname || '').toLowerCase();
  };

  const getDomainDefaultCurrency = (host: string): Currency | null => {
    // Explicit business rules by domain.
    if (host.includes('stringpak.ie') || host.includes('stringpak-co-uk') || host.includes('stringpak.eu')) {
      return 'EUR';
    }
    if (host.includes('stringpak.com') || host.includes('stringpak-com') || host.includes('stringpak.net')) {
      return 'USD';
    }
    return null;
  };

  const getPerDomainStorageKey = (host: string) => `selectedCurrency:${host || 'default'}`;

  // Auto-detect currency based on domain first, then user/device hints.
  useEffect(() => {
    const detectCurrency = async () => {
      try {
        const host = getHostName();
        const domainDefault = getDomainDefaultCurrency(host);
        const perDomainKey = getPerDomainStorageKey(host);

        // Domain-specific saved preference wins for returning visitors on same domain.
        const savedPerDomain = localStorage.getItem(perDomainKey) as Currency | null;
        if (savedPerDomain && CURRENCIES[savedPerDomain]) {
          setCurrencyState(savedPerDomain);
          localStorage.setItem('selectedCurrency', savedPerDomain);
          return;
        }

        // Domain default wins over language/timezone for first-time visits on known domains.
        if (domainDefault) {
          setCurrencyState(domainDefault);
          localStorage.setItem(perDomainKey, domainDefault);
          localStorage.setItem('selectedCurrency', domainDefault);
          return;
        }

        // Fallback auto-detect based on language/timezone.
        const language = navigator.language || 'en-US';
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        let detectedCurrency: Currency = 'EUR';
        if (language.includes('en-GB') || timezone.includes('Europe/London')) {
          detectedCurrency = 'GBP';
        } else if (language.includes('en-US') || timezone.includes('America/')) {
          detectedCurrency = 'USD';
        } else if (
          language.includes('de') ||
          language.includes('fr') ||
          language.includes('es') ||
          timezone.includes('Europe/')
        ) {
          detectedCurrency = 'EUR';
        }

        setCurrencyState(detectedCurrency);
        localStorage.setItem(perDomainKey, detectedCurrency);
        localStorage.setItem('selectedCurrency', detectedCurrency);
      } catch (error) {
        console.error('Error detecting currency:', error);
        setCurrencyState('EUR');
      } finally {
        setLoading(false);
      }
    };

    detectCurrency();
  }, []);

  const setCurrency = (newCurrency: Currency) => {
    setCurrencyState(newCurrency);
    const host = getHostName();
    localStorage.setItem(getPerDomainStorageKey(host), newCurrency);
    localStorage.setItem('selectedCurrency', newCurrency);
  };

  // Store/product prices are in USD; convert to display currency with exact 2-decimal rounding
  const convertPrice = (price: number, fromCurrency: Currency = 'USD'): number => {
    if (fromCurrency === currency) return Math.round(price * 100) / 100;
    // Convert: source → EUR (base) → display currency; then round to exact cents
    const eurPrice = fromCurrency === 'EUR' ? price : price / CURRENCIES[fromCurrency].exchangeRate;
    const raw = eurPrice * CURRENCIES[currency].exchangeRate;
    return Math.round(raw * 100) / 100;
  };

  const formatPrice = (price: number): string => {
    const convertedPrice = convertPrice(price);
    const currencyConfig = CURRENCIES[currency];
    return new Intl.NumberFormat(currencyConfig.locale, {
      style: 'currency',
      currency: currencyConfig.code,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(convertedPrice);
  };

  const value = {
    currency,
    setCurrency,
    formatPrice,
    convertPrice,
    currencies: Object.values(CURRENCIES),
    loading,
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};
